import { FunctionType, PermissionType } from '../../../../const/UserPermission';
import {
  AuthorizationType,
  ChapterFragment,
  ChapterSearchFragment,
  ChapterForCourseProgramStepItemFragment,
  PracticeFragment,
  PracticeListItemFragment,
  PracticeSearchFragment,
  PracticeForCourseProgramStepItemFragment,
  Program,
  ProgramElementForCourseProgramStepItemFragment,
  ProgramDetailFragment,
  ProgramFragment,
  ProgramSearchFragment,
  ExamMedalType,
} from '../../../../gen/graphql';
import { ChapterAndPracticeProgramElements, ProgramElementInProgramDetail } from '../../../../types/Program';
import GoldMedal from '../../../../static/image/medal_gold.svg';
import SilverMedal from '../../../../static/image/medal_silver.svg';
import BronzeMedal from '../../../../static/image/medal_bronze.svg';
import BlankMedal from '../../../../static/image/medal_blank.svg';

export function isCompletedProgram(programElements?: ChapterAndPracticeProgramElements): boolean {
  if (!programElements || programElements.length === 0) {
    return false;
  }

  return programElements.every(({ chapter, practice }) => chapter?.studyLog || practice?.mySubmit);
}

export function isLearnedProgram(programElements?: ChapterAndPracticeProgramElements): boolean {
  if (!programElements || programElements.length === 0) {
    return false;
  }

  return programElements.some(({ chapter, practice }) => chapter?.studyLog || practice?.mySubmit);
}

export function getChapterFunctionType(type: AuthorizationType | undefined): string | undefined {
  switch (type) {
    case AuthorizationType.Public:
      return FunctionType.All;
    case AuthorizationType.Registered:
      return FunctionType.ChapterAuthTypeRegistered;
    case AuthorizationType.Light:
      return FunctionType.ChapterAuthTypeLight;
    case AuthorizationType.Basic:
      return FunctionType.ChapterAuthTypeBasic;
    case AuthorizationType.Premium:
      return FunctionType.ChapterAuthTypePremium;
    default:
      return undefined;
  }
}

export function getPracticeFunctionType(type: AuthorizationType): string | undefined {
  switch (type) {
    case AuthorizationType.Public:
      return FunctionType.All;
    case AuthorizationType.Registered:
      return FunctionType.PracticeAuthTypeRegistered;
    case AuthorizationType.Light:
      return FunctionType.PracticeAuthTypeLight;
    case AuthorizationType.Basic:
      return FunctionType.PracticeAuthTypeBasic;
    case AuthorizationType.Premium:
      return FunctionType.PracticeAuthTypePremium;
    default:
      return undefined;
  }
}

export function getChapterElementFunctionType(
  programElement: ProgramElementForCourseProgramStepItemFragment | undefined,
): string | undefined {
  if (programElement?.chapter) {
    return getChapterFunctionType(programElement.chapter.type);
  }
  if (programElement?.practice) {
    return getPracticeFunctionType(programElement.practice.type);
  }

  return undefined;
}

export function checkProgramElementAuthType(
  programElement: ProgramElementForCourseProgramStepItemFragment | ProgramElementInProgramDetail | undefined,
  permissionCheck: (functionType: string, permissionType: string) => boolean,
): boolean {
  if (programElement?.chapter) {
    return checkChapterAuthType(programElement.chapter, permissionCheck);
  }
  if (programElement?.practice) {
    return checkPracticeAuthType(programElement.practice, permissionCheck);
  }

  return false;
}

export function checkChapterAuthType(
  chapter: ChapterFragment | ChapterSearchFragment | ChapterForCourseProgramStepItemFragment | undefined,
  permissionCheck: (functionType: string, permissionType: string) => boolean,
): boolean {
  switch (chapter?.type) {
    case AuthorizationType.Public:
      return true;
    case AuthorizationType.Registered:
      return permissionCheck(FunctionType.ChapterAuthTypeRegistered, PermissionType.Read);
    case AuthorizationType.Light:
      return permissionCheck(FunctionType.ChapterAuthTypeLight, PermissionType.Read);
    case AuthorizationType.Basic:
      return permissionCheck(FunctionType.ChapterAuthTypeBasic, PermissionType.Read);
    case AuthorizationType.Premium:
      return permissionCheck(FunctionType.ChapterAuthTypePremium, PermissionType.Read);
    default:
      return false;
  }
}

export function checkPracticeAuthType(
  practice:
    | PracticeFragment
    | PracticeListItemFragment
    | PracticeSearchFragment
    | PracticeForCourseProgramStepItemFragment,
  permissionCheck: (functionType: string, permissionType: string) => boolean,
): boolean {
  switch (practice.type) {
    case AuthorizationType.Public:
      return true;
    case AuthorizationType.Registered:
      return permissionCheck(FunctionType.PracticeAuthTypeRegistered, PermissionType.Read);
    case AuthorizationType.Light:
      return permissionCheck(FunctionType.PracticeAuthTypeLight, PermissionType.Read);
    case AuthorizationType.Basic:
      return permissionCheck(FunctionType.PracticeAuthTypeBasic, PermissionType.Read);
    case AuthorizationType.Premium:
      return permissionCheck(FunctionType.PracticeAuthTypePremium, PermissionType.Read);
    default:
      return false;
  }
}

export function getChapters(
  program?: Program | ProgramFragment | ProgramDetailFragment | ProgramSearchFragment,
): (ChapterFragment | ChapterSearchFragment)[] | undefined {
  if (!program?.programElements) return undefined;

  const chapters = program.programElements
    .map((item) => item.chapter)
    .filter((item) => item !== undefined)
    .filter((item) => item !== null);

  return chapters;
}

export function getPractices(
  program?: Program | ProgramFragment | ProgramDetailFragment | ProgramSearchFragment,
): PracticeSearchFragment[] | undefined {
  if (!program?.programElements) return undefined;

  const practices = program.programElements
    .map((item) => item.practice)
    .filter((item) => item !== undefined)
    .filter((item) => item !== null);

  return practices;
}

export function getChaptersAndPractices(
  program: Program | ProgramFragment | ProgramDetailFragment | ProgramSearchFragment | null | undefined,
): (ChapterFragment | ChapterSearchFragment | PracticeFragment | PracticeSearchFragment)[] {
  if (!program?.programElements) return [];

  const programElements = program.programElements
    .map((item) => {
      if (item.chapter) return item.chapter;
      if (item.practice) return item.practice;
    })
    .filter((item) => item !== undefined)
    .filter((item) => item !== null);

  return programElements;
}

export function getMedalImage(medalType: ExamMedalType | undefined): string {
  switch (medalType) {
    case ExamMedalType.Gold:
      return GoldMedal;
    case ExamMedalType.Silver:
      return SilverMedal;
    case ExamMedalType.Bronze:
      return BronzeMedal;
    default:
      return BlankMedal;
  }
}
